// src/pages/Community/Contact.js
import React, { useState } from 'react';
import styled from 'styled-components';
import NaverMap from '../../components/NaverMap';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../components/common/AnimatedSection';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 0;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  margin-bottom: 1rem;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #4b5563;
  font-weight: 400;
  line-height: 1.6;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    white-space: pre-line;
  }
`;

const ContactFormSection = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(59, 131, 246, 0.1);
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const ContactForm = styled.form`
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 2rem;
  font-weight: 700;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.8rem;
  margin-bottom: 1.8rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 1rem;
  background: #f8fafc;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 1rem;
  background: #f8fafc;
  min-height: 200px;
  resize: vertical;
  margin-bottom: 2rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SubmitButton = styled.button`
  background: #60a5fa;
  color: #ffffff;
  padding: 1.2rem 2rem;
  border: none;
  border-radius: 0;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
  width: auto;
  min-width: 150px;
  margin-left: auto;
  display: block;

  &:hover {
    background: #3b82f6;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    opacity: .6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: #4b5563;

  input[type="checkbox"] {
    margin-right: 0.5rem;
  }
`;

const InfoMapSection = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
  padding-right: 2.5rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 2rem;
  font-weight: 700;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  color: #222222;

  .icon {
    width: 24px;
    height: 24px;
    margin-right: 1rem;
    color: #3b82f6;
    font-size: 1.2rem;
  }
`;

const MapArea = styled.div`
  background: #e5edfa;
  border-radius: 0;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1.1rem;
  border: 1px solid #e5e7eb;
  margin: auto 0;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const contactInfo = [
  { icon: '📍', title: '주소', content: '광주광역시 북구 하서로 672번길 18-17\n(지동 806-9)' },
  { icon: '📞', title: '전화번호', content: '062-511-7741' },
  { icon: '📠', title: '팩스', content: '062-576-7741' },
  { icon: '📧', title: '이메일', content: 'seyeon7741@daum.net' },
];

function Contact() {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [formRef, formVisible] = useScrollAnimation();
  const [infoRef, infoVisible] = useScrollAnimation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // 프록시 사용 시 ''(상대경로)로 두세요. 배포에서 별도 API 도메인이면 .env에 REACT_APP_API_BASE 지정
  const API = process.env.REACT_APP_API_BASE || '';

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    if (!formData.name.trim()) return '성함을 입력해 주세요.';
    if (!formData.email.trim()) return '이메일을 입력해 주세요.';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return '이메일 형식을 확인해 주세요.';
    if (!formData.message.trim()) return '문의 내용을 입력해 주세요.';
    if (!formData.privacy) return '개인정보처리방침에 동의해 주세요.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
          message: formData.message.trim(),
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `요청 실패 (status ${res.status})`);
      }

      alert('문의가 접수되었습니다. 빠른 시일 내에 답변드리겠습니다.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        privacy: false,
      });
    } catch (err2) {
      console.error(err2);
      alert('전송에 실패했습니다.\n' + (err2.message || '잠시 후 다시 시도해 주세요.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <PageContainer>
        <Container>
          <AnimatedSection ref={headerRef} isVisible={headerVisible} animation="fadeInUp" clickable>
            <PageHeader>
              <PageTitle>문의하기</PageTitle>
              <PageSubtitle>궁금한 사항이 있으시면 언제든지 문의해 주세요</PageSubtitle>
            </PageHeader>
          </AnimatedSection>

          {/* Contact Form - 상단 */}
          <AnimatedSection
              ref={formRef}
              isVisible={formVisible}
              animation="fadeInUp"
              delay="0.2s"
              clickable
          >
            <ContactFormSection>
              <ContactForm onSubmit={handleSubmit}>
                <FormTitle>1:1 문의</FormTitle>

                <FormGrid>
                  <InputField
                      type="text"
                      name="name"
                      placeholder="성함 *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                  />
                  <InputField
                      type="email"
                      name="email"
                      placeholder="이메일 *"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                  />
                </FormGrid>

                <FormGrid>
                  <InputField
                      type="tel"
                      name="phone"
                      placeholder="연락처"
                      value={formData.phone}
                      onChange={handleInputChange}
                  />
                  <InputField
                      type="text"
                      name="company"
                      placeholder="회사명"
                      value={formData.company}
                      onChange={handleInputChange}
                  />
                </FormGrid>

                <TextArea
                    name="message"
                    placeholder="문의 내용을 입력해 주세요 *"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                />

                <CheckboxWrapper>
                  <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleInputChange}
                      required
                  />
                  <label>개인정보처리방침에 동의합니다 *</label>
                </CheckboxWrapper>

                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? '전송 중...' : '문의 보내기'}
                </SubmitButton>
              </ContactForm>
            </ContactFormSection>
          </AnimatedSection>

          {/* Info & Map - 하단 */}
          <AnimatedSection ref={infoRef} isVisible={infoVisible} animation="scaleIn" delay="0.4s" clickable>
            <InfoMapSection>
              <ContactGrid>
                <ContactInfo>
                  <InfoTitle>연락처 정보</InfoTitle>
                  {contactInfo.map((info, index) => (
                      <InfoItem key={index}>
                        <span className="icon">{info.icon}</span>
                        <div>
                          <strong>{info.title}</strong>
                          <br />
                          {info.content.split('\n').map((line, i) => (
                              <span key={i}>
                          {line}
                                {i < info.content.split('\n').length - 1 && <br />}
                        </span>
                          ))}
                        </div>
                      </InfoItem>
                  ))}
                </ContactInfo>

                <MapArea>
                  <NaverMap />
                </MapArea>
              </ContactGrid>
            </InfoMapSection>
          </AnimatedSection>
        </Container>
      </PageContainer>
  );
}

export default Contact;
